import { BaseElement } from '../../../base-element';
import type { EntryPricingQuoteDto, TrialEventRegistrationConfigDto } from '../../../../api/types';
import type { ValidationError } from '../../../../state/form.state';
import { handlerReference, type RunSelectionDraft } from '../../../../services/run-selections';

const divisions = [
  ['std', 'STD', 'Started'], ['opn', 'OPN', 'Open'], ['adv', 'ADV', 'Advanced'],
  ['ftdOpn', 'FTD_OPN', 'FTD Open'], ['ftdAdv', 'FTD_ADV', 'FTD Advanced'],
  ['nov', 'NOV', 'Novice'], ['wrkJrHndlr', 'WRK_JR_HNDLR', 'Working Junior Handler'],
  ['postAdv', 'POST_ADV', 'Post Advanced'], ['rtd', 'RTD', 'RTD'],
] as const;

export interface ActualRunsEditorData {
  config: TrialEventRegistrationConfigDto | null;
  selections: RunSelectionDraft;
  quote: EntryPricingQuoteDto | null;
  errors: ValidationError[];
  disabled: boolean;
}

export class ActualRunsEditor extends BaseElement {
  private model: ActualRunsEditorData = {
    config: null, selections: { actualHandlers: [], runs: [] }, quote: null, errors: [], disabled: false,
  };

  set data(value: ActualRunsEditorData) {
    this.model = value;
    this.updateRender();
  }

  connectedCallback(): void { this.updateRender(); }

  private change(selections: RunSelectionDraft): void {
    this.model = { ...this.model, selections, quote: null };
    this.emit('runs-change', selections);
  }

  private input(path: string, label: string, value: string, type = 'text'): string {
    const error = this.model.errors.find(item => item.field === path)?.message;
    return `<label class="grid min-w-0 gap-1 text-sm">${label}
      <input class="w-full min-w-0 rounded-lg border border-neutral-300 p-2" type="${type}"
        value="${this.esc(value)}" aria-label="${this.esc(label)}" data-control-path="${path}" ${error ? 'aria-invalid="true"' : ''}>
      ${error ? `<span class="text-error-700">${this.esc(error)}</span>` : ''}
    </label>`;
  }

  private updateRender(): void {
    const { config, selections, disabled } = this.model;
    const { actualHandlers, runs } = selections;
    const restoreFocus = this.retainControlFocus();
    this.render(`
      <section class="grid min-w-0 gap-4" aria-label="Actual trial runs">
        <h2 class="text-lg font-semibold">Actual trial runs</h2>
        <p class="text-sm">Choose each run's actual handler explicitly. Entry contact and notification details are separate.
          These choices record runs, not qualification certification. Host eligibility and signature checks still apply.</p>
        <fieldset class="grid min-w-0 gap-4" ${disabled ? 'disabled' : ''}>
          <legend class="font-semibold">Actual handlers</legend>
          ${actualHandlers.map((handler, index) => `
            <fieldset class="grid min-w-0 gap-3 rounded-xl border border-neutral-200 p-3 sm:grid-cols-2">
              <legend class="font-medium">Actual handler ${index + 1}</legend>
              ${this.input(`selections.actualHandlers[${index}].name`, 'Actual handler name', handler.name ?? '')}
              ${this.input(`selections.actualHandlers[${index}].dob`, 'Actual handler DOB (required for Junior)', handler.dob ?? '', 'date')}
              ${this.input(`selections.actualHandlers[${index}].membershipNumber`, 'Actual handler membership number (optional)', handler.membershipNumber ?? '')}
              <button type="button" class="text-left text-error-700 underline" data-remove-handler="${index}"
                ${runs.some(run => (run.actualHandlerId ?? run.clientHandlerKey) === handlerReference(handler)) ? 'disabled' : ''}>
                Remove actual handler ${index + 1}</button>
            </fieldset>
          `).join('')}
          <button type="button" class="justify-self-start rounded-lg border border-gray-200 p-2" data-add-handler>Add actual handler</button>
          ${config ? config.actualTrials.map(trial => `
            <fieldset class="grid min-w-0 gap-3 rounded-xl border border-neutral-200 p-3" data-actual-trial="${this.esc(trial.actualTrialId)}">
              <legend class="font-semibold">${this.esc(trial.date)} / Trial ${trial.trialNumberOnDate}</legend>
              ${trial.stocks.length ? trial.stocks.map(offer => `
                <div class="grid min-w-0 gap-2">
                  <p class="text-sm">${this.esc(offer.stock)}: ${offer.headCount} head;
                    pre-entry ${this.money(offer.preEntryFee)}; day-of ${this.money(offer.dayOfFee)}.
                    Count is not capacity.</p>
                  <button type="button" class="justify-self-start rounded-lg border border-gray-200 p-2"
                    data-add-run="${this.esc(trial.actualTrialId)}" data-stock="${this.esc(offer.stock)}">Add ${this.esc(offer.stock)} run</button>
                </div>
              `).join('') : '<p>No stock offered at this trial.</p>'}
            </fieldset>
          `).join('') : '<p role="alert">Current actual-trial configuration is unavailable. Reload before editing.</p>'}
          ${runs.map((run, index) => {
            const trial = config?.actualTrials.find(item => item.actualTrialId === run.actualTrialId);
            const offer = trial?.stocks.find(item => item.stock === run.stock);
            const choices = divisions.filter(([key]) => offer?.classes[key]);
            const reference = run.actualHandlerId ?? run.clientHandlerKey ?? '';
            const prefix = `selections.runs[${index}]`;
            const item = this.model.quote?.items.find(item => item.entryRunId === run.entryRunId);
            return `<fieldset class="grid min-w-0 gap-3 rounded-xl border border-neutral-300 p-3 sm:grid-cols-2" data-run="${index}">
              <legend class="font-semibold">Run ${index + 1}: ${this.esc(trial ? `${trial.date} / Trial ${trial.trialNumberOnDate}` : run.actualTrialId ?? 'Missing trial')} / ${this.esc(run.stock ?? 'Missing stock')}</legend>
              ${!offer ? '<p class="text-error-700">This saved offer is no longer available. Remove this run or reload the configuration; it has not been dropped.</p>' : ''}
              <label class="grid min-w-0 gap-1 text-sm">Division
                <select class="w-full min-w-0 rounded-lg border border-gray-200 p-2" aria-label="Division" data-control-path="${prefix}.division">
                  <option value="">Choose division</option>
                  ${run.division && !choices.some(([, token]) => token === run.division) ? `<option value="${this.esc(run.division)}" selected>${this.esc(run.division)} (no longer offered)</option>` : ''}
                  ${choices.map(([, token, label]) => `<option value="${token}" ${run.division === token ? 'selected' : ''}>${label}</option>`).join('')}
                </select>
              </label>
              <label class="grid min-w-0 gap-1 text-sm">Actual handler
                <select class="w-full min-w-0 rounded-lg border border-gray-200 p-2" aria-label="Actual handler" data-control-path="${prefix}.actualHandlerId">
                  <option value="">Choose actual handler</option>
                  ${actualHandlers.map((handler, handlerIndex) => `<option value="${this.esc(handlerReference(handler))}" ${reference === handlerReference(handler) ? 'selected' : ''}>${handlerIndex + 1}: ${this.esc(handler.name || 'Unnamed handler')}</option>`).join('')}
                </select>
              </label>
              <label class="flex items-start gap-2 text-sm"><input type="checkbox" data-control-path="${prefix}.isFeo"
                ${run.isFeo ? 'checked' : ''} ${!offer?.allowsFeo && !run.isFeo ? 'disabled' : ''}>FEO (For Exhibition Only)</label>
              <p class="text-sm">${item ? `Server line item: ${this.money(item.lineAmount)} (${this.money(item.unitRate)} per run)` : 'Save runs to obtain a server line item.'}</p>
              <button type="button" class="justify-self-start text-error-700 underline" data-remove-run="${index}">Remove run ${index + 1}</button>
            </fieldset>`;
          }).join('')}
          ${!runs.length ? '<p>No runs selected.</p>' : ''}
        </fieldset>
      </section>
    `);
    this.querySelector('[data-add-handler]')?.addEventListener('click', () => {
      this.change({ ...structuredClone(selections), actualHandlers: [...actualHandlers, {
        actualHandlerId: null, clientHandlerKey: crypto.randomUUID(), name: '', dob: null, membershipNumber: null,
      }] });
    });
    this.querySelectorAll<HTMLButtonElement>('[data-remove-handler]').forEach(button => {
      button.addEventListener('click', () => this.change({ ...structuredClone(selections),
        actualHandlers: actualHandlers.filter((_, index) => index !== Number(button.dataset.removeHandler)) }));
    });
    this.querySelectorAll<HTMLButtonElement>('[data-add-run]').forEach(button => {
      button.disabled ||= !config || disabled;
      button.addEventListener('click', () => this.change({ ...structuredClone(selections), runs: [...runs, {
        entryRunId: null, actualTrialId: button.dataset.addRun!, stock: button.dataset.stock!,
        division: null, isFeo: false, actualHandlerId: null, clientHandlerKey: null, displayOrder: runs.length,
      }] }));
    });
    this.querySelectorAll<HTMLButtonElement>('[data-remove-run]').forEach(button => {
      button.addEventListener('click', () => this.change({ ...structuredClone(selections),
        runs: runs.filter((_, index) => index !== Number(button.dataset.removeRun))
          .map((run, index) => ({ ...run, displayOrder: index })) }));
    });
    this.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-control-path]').forEach(control => {
      control.addEventListener('change', () => {
        const next = structuredClone(this.model.selections);
        const path = control.dataset.controlPath!;
        const match = /^selections\.(actualHandlers|runs)\[(\d+)\]\.(\w+)$/.exec(path)!;
        const index = Number(match[2]);
        const field = match[3];
        if (match[1] === 'actualHandlers') {
          const handler = next.actualHandlers[index];
          if (field === 'name') handler.name = control.value;
          if (field === 'dob') handler.dob = control.value || null;
          if (field === 'membershipNumber') handler.membershipNumber = control.value || null;
        } else {
          const run = next.runs[index];
          if (field === 'division') run.division = control.value || null;
          if (field === 'isFeo' && control instanceof HTMLInputElement) run.isFeo = control.checked;
          if (field === 'actualHandlerId') {
            const handler = next.actualHandlers.find(item => handlerReference(item) === control.value);
            run.actualHandlerId = handler?.actualHandlerId ?? null;
            run.clientHandlerKey = handler?.clientHandlerKey ?? null;
          }
        }
        this.change(next);
      });
      if (control instanceof HTMLInputElement && control.type !== 'checkbox') {
        control.addEventListener('input', () => {
          const path = control.dataset.controlPath!;
          const match = /^selections\.actualHandlers\[(\d+)\]\.(\w+)$/.exec(path);
          if (!match) return;
          const next = structuredClone(this.model.selections);
          const handler = next.actualHandlers[Number(match[1])];
          if (match[2] === 'name') handler.name = control.value;
          if (match[2] === 'dob') handler.dob = control.value || null;
          if (match[2] === 'membershipNumber') handler.membershipNumber = control.value || null;
          this.change(next);
        });
      }
    });
    restoreFocus();
  }

  private money(value: number): string {
    return `${value.toFixed(2)} USD`;
  }
}

customElements.define('dt-actual-runs-editor', ActualRunsEditor);
